'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoTooltip } from '@/comps/info-tooltip';
import { Button } from '@/comps/primitives/button';
import { CalculationTable } from '@/domains/pages/model-savings/components/calculation-table';
import { ModelMixBars } from '@/domains/pages/model-savings/components/model-mix-bars';
import { RecommendedMixLabel } from '@/domains/pages/model-savings/components/recommended-mix-label';
import { MODEL_SAVINGS_POSITIVE_COLOR, MODEL_SAVINGS_STAT_NUMBER_CLASS, MODEL_SAVINGS_STAT_SUFFIX_CLASS, SAVINGS_DETAILS_SECTION_TOOLTIPS } from '@/domains/pages/model-savings/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import type { ModelMixSegment, SavingsCalculationRow, SavingsScopeData, SavingsTask } from '@/domains/pages/model-savings/types';
import { computeLighterCouldUsePercent, formatAnnualSavings, formatAnnualSpend, formatCount, mixPercent } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { ChevronLeft, ChevronRight } from 'lucide-react';
type SavingsDetailsContentProps = {
  title: string;
  subtitle: string;
  data: SavingsScopeData | SavingsTask;
  calculationRows?: SavingsCalculationRow[];
  calculationTotalActualSpend?: number;
  calculationTotalOptimalSpend?: number;
  isCalculationLoading?: boolean;
  actualMixOverride?: ModelMixSegment[];
  optimalMixOverride?: ModelMixSegment[];
  showNavigation?: boolean;
  canGoPrev?: boolean;
  canGoNext?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
};
function isTaskData(data: SavingsScopeData | SavingsTask): data is SavingsTask {
  return 'name' in data && !('segments' in data);
}
export function SavingsDetailsContent(t0) {
  const $ = _c(142);
  const {
    title,
    subtitle,
    data,
    calculationRows,
    calculationTotalActualSpend,
    calculationTotalOptimalSpend,
    isCalculationLoading: t1,
    actualMixOverride,
    optimalMixOverride,
    showNavigation: t2,
    canGoPrev: t3,
    canGoNext: t4,
    onPrev,
    onNext
  } = t0;
  const isCalculationLoading = t1 === undefined ? false : t1;
  const showNavigation = t2 === undefined ? false : t2;
  const canGoPrev = t3 === undefined ? false : t3;
  const canGoNext = t4 === undefined ? false : t4;
  const savingAnnual = data.savingAnnual;
  const spendAnnual = data.spendAnnual;
  const savingSharePercent = isTaskData(data) ? data.savingSharePercent : data.savingSharePercent;
  const actualMix = actualMixOverride ?? data.actualMix;
  const optimalMix = optimalMixOverride ?? data.optimalMix;
  const recommendedMix = optimalMixOverride ?? data.recommendedMix;
  let resolvedCalculationRows;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let tableTotalActualSpend;
  let tableTotalDelta;
  let tableTotalOptimalSpend;
  if ($[0] !== actualMix || $[1] !== calculationRows || $[2] !== calculationTotalActualSpend || $[3] !== calculationTotalOptimalSpend || $[4] !== canGoNext || $[5] !== canGoPrev || $[6] !== onNext || $[7] !== onPrev || $[8] !== optimalMix || $[9] !== savingAnnual || $[10] !== showNavigation || $[11] !== spendAnnual || $[12] !== subtitle || $[13] !== title) {
    const frontierSharePercent = mixPercent(actualMix, "opus");
    const frontierNeededPercent = mixPercent(optimalMix, "opus");
    const lighterCouldUsePercent = computeLighterCouldUsePercent(actualMix, optimalMix);
    let t22;
    if ($[35] !== calculationRows) {
      t22 = calculationRows ?? [];
      $[35] = calculationRows;
      $[36] = t22;
    } else {
      t22 = $[36];
    }
    resolvedCalculationRows = t22;
    let t23;
    if ($[37] !== calculationTotalActualSpend || $[38] !== resolvedCalculationRows || $[39] !== spendAnnual) {
      t23 = calculationTotalActualSpend ?? (resolvedCalculationRows.length > 0 ? resolvedCalculationRows.reduce(_temp, 0) : spendAnnual);
      $[37] = calculationTotalActualSpend;
      $[38] = resolvedCalculationRows;
      $[39] = spendAnnual;
      $[40] = t23;
    } else {
      t23 = $[40];
    }
    tableTotalActualSpend = t23;
    let t24;
    if ($[41] !== calculationTotalOptimalSpend || $[42] !== resolvedCalculationRows || $[43] !== savingAnnual || $[44] !== spendAnnual) {
      t24 = calculationTotalOptimalSpend ?? (resolvedCalculationRows.length > 0 ? resolvedCalculationRows.reduce(_temp2, 0) : spendAnnual - savingAnnual);
      $[41] = calculationTotalOptimalSpend;
      $[42] = resolvedCalculationRows;
      $[43] = savingAnnual;
      $[44] = spendAnnual;
      $[45] = t24;
    } else {
      t24 = $[45];
    }
    tableTotalOptimalSpend = t24;
    tableTotalDelta = tableTotalOptimalSpend - tableTotalActualSpend;
    t20 = "flex min-h-0 flex-1 flex-col overflow-hidden";
    let t25;
    if ($[46] !== title) {
      t25 = <h2 className="text-lg leading-tight font-semibold text-foreground">{title}</h2>;
      $[46] = title;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    let t26;
    if ($[48] !== subtitle) {
      t26 = <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>;
      $[48] = subtitle;
      $[49] = t26;
    } else {
      t26 = $[49];
    }
    let t27;
    if ($[50] !== t25 || $[51] !== t26) {
      t27 = <div className="min-w-0">{t25}{t26}</div>;
      $[50] = t25;
      $[51] = t26;
      $[52] = t27;
    } else {
      t27 = $[52];
    }
    let t28;
    if ($[53] !== canGoNext || $[54] !== canGoPrev || $[55] !== onNext || $[56] !== onPrev || $[57] !== showNavigation) {
      t28 = showNavigation ? <div className="flex shrink-0 items-center gap-1"><Button variant="ghost" size="icon" className="size-8" disabled={!canGoPrev} onClick={onPrev}><ChevronLeft className="size-4" /></Button><Button variant="ghost" size="icon" className="size-8" disabled={!canGoNext} onClick={onNext}><ChevronRight className="size-4" /></Button></div> : null;
      $[53] = canGoNext;
      $[54] = canGoPrev;
      $[55] = onNext;
      $[56] = onPrev;
      $[57] = showNavigation;
      $[58] = t28;
    } else {
      t28 = $[58];
    }
    if ($[59] !== t27 || $[60] !== t28) {
      t21 = <div className="shrink-0 border-b border-b-lightborder px-6 py-4"><div className="flex items-start justify-between gap-3">{t27}{t28}</div></div>;
      $[59] = t27;
      $[60] = t28;
      $[61] = t21;
    } else {
      t21 = $[61];
    }
    t18 = "min-h-0 flex-1 overflow-y-auto px-6 py-5";
    let t29;
    let t30;
    let t31;
    let t32;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = {
        borderColor: MODEL_SAVINGS_POSITIVE_COLOR
      };
      t30 = <p className="text-[10px] text-muted-foreground">Savings opportunity</p>;
      t31 = cn("mt-1", MODEL_SAVINGS_STAT_NUMBER_CLASS, AIFI_DATA_NUMBER_CLASS);
      t32 = {
        color: MODEL_SAVINGS_POSITIVE_COLOR
      };
      $[62] = t29;
      $[63] = t30;
      $[64] = t31;
      $[65] = t32;
    } else {
      t29 = $[62];
      t30 = $[63];
      t31 = $[64];
      t32 = $[65];
    }
    let t33;
    if ($[66] !== savingAnnual) {
      t33 = formatAnnualSavings(savingAnnual);
      $[66] = savingAnnual;
      $[67] = t33;
    } else {
      t33 = $[67];
    }
    let t34;
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <span className={MODEL_SAVINGS_STAT_SUFFIX_CLASS}>/Y</span>;
      $[68] = t34;
    } else {
      t34 = $[68];
    }
    let t35;
    if ($[69] !== t33) {
      t35 = <div className="rounded-lg border px-4 py-3" style={t29}>{t30}<p className={t31} style={t32}>{t33}{t34}</p></div>;
      $[69] = t33;
      $[70] = t35;
    } else {
      t35 = $[70];
    }
    let t36;
    let t37;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <p className="text-[10px] text-muted-foreground">Actual AI spend</p>;
      t37 = cn("mt-1 text-foreground", MODEL_SAVINGS_STAT_NUMBER_CLASS, AIFI_DATA_NUMBER_CLASS);
      $[71] = t36;
      $[72] = t37;
    } else {
      t36 = $[71];
      t37 = $[72];
    }
    let t38;
    if ($[73] !== spendAnnual) {
      t38 = formatAnnualSpend(spendAnnual);
      $[73] = spendAnnual;
      $[74] = t38;
    } else {
      t38 = $[74];
    }
    let t39;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <span className={MODEL_SAVINGS_STAT_SUFFIX_CLASS}>/Y</span>;
      $[75] = t39;
    } else {
      t39 = $[75];
    }
    let t40;
    if ($[76] !== t38) {
      t40 = <div className="rounded-lg border border-lightborder px-4 py-3">{t36}<p className={t37}>{t38}{t39}</p></div>;
      $[76] = t38;
      $[77] = t40;
    } else {
      t40 = $[77];
    }
    if ($[78] !== t35 || $[79] !== t40) {
      t19 = <div className="grid grid-cols-2 gap-3">{t35}{t40}</div>;
      $[78] = t35;
      $[79] = t40;
      $[80] = t19;
    } else {
      t19 = $[80];
    }
    t7 = "-mx-6 mt-5 border-t border-t-lightborder px-6 pt-5 text-sm leading-relaxed text-muted-foreground";
    t8 = "Frontier models account for";
    t9 = " ";
    let t41;
    if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = cn("font-medium text-foreground", AIFI_DATA_NUMBER_CLASS);
      $[81] = t41;
    } else {
      t41 = $[81];
    }
    const t42 = formatScore(frontierSharePercent, {
      decimals: 0
    });
    if ($[82] !== t42) {
      t10 = <span className={t41}>{t42}%</span>;
      $[82] = t42;
      $[83] = t10;
    } else {
      t10 = $[83];
    }
    t11 = " ";
    t12 = "of actual AI spend, but only";
    t13 = " ";
    let t43;
    if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = cn("font-medium text-foreground", AIFI_DATA_NUMBER_CLASS);
      $[84] = t43;
    } else {
      t43 = $[84];
    }
    const t44 = formatScore(frontierNeededPercent, {
      decimals: 0
    });
    if ($[85] !== t44) {
      t14 = <span className={t43}>{t44}%</span>;
      $[85] = t44;
      $[86] = t14;
    } else {
      t14 = $[86];
    }
    t15 = " ";
    t16 = "of catalog cost would need that level of capability. Meanwhile,";
    t17 = " ";
    if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("font-medium text-foreground", AIFI_DATA_NUMBER_CLASS);
      $[87] = t5;
    } else {
      t5 = $[87];
    }
    t6 = formatScore(lighterCouldUsePercent, {
      decimals: 0
    });
    $[0] = actualMix;
    $[1] = calculationRows;
    $[2] = calculationTotalActualSpend;
    $[3] = calculationTotalOptimalSpend;
    $[4] = canGoNext;
    $[5] = canGoPrev;
    $[6] = onNext;
    $[7] = onPrev;
    $[8] = optimalMix;
    $[9] = savingAnnual;
    $[10] = showNavigation;
    $[11] = spendAnnual;
    $[12] = subtitle;
    $[13] = title;
    $[14] = resolvedCalculationRows;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
    $[26] = t21;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = tableTotalActualSpend;
    $[33] = tableTotalDelta;
    $[34] = tableTotalOptimalSpend;
  } else {
    resolvedCalculationRows = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
    t21 = $[26];
    t5 = $[27];
    t6 = $[28];
    t7 = $[29];
    t8 = $[30];
    t9 = $[31];
    tableTotalActualSpend = $[32];
    tableTotalDelta = $[33];
    tableTotalOptimalSpend = $[34];
  }
  let t22;
  if ($[88] !== t5 || $[89] !== t6) {
    t22 = <span className={t5}>{t6}%</span>;
    $[88] = t5;
    $[89] = t6;
    $[90] = t22;
  } else {
    t22 = $[90];
  }
  let t23;
  let t24;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = cn("font-semibold", AIFI_DATA_NUMBER_CLASS);
    t24 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[91] = t23;
    $[92] = t24;
  } else {
    t23 = $[91];
    t24 = $[92];
  }
  let t25;
  if ($[93] !== savingAnnual) {
    t25 = formatAnnualSavings(savingAnnual);
    $[93] = savingAnnual;
    $[94] = t25;
  } else {
    t25 = $[94];
  }
  let t26;
  if ($[95] !== t25) {
    t26 = <span className={t23} style={t24}>{t25}</span>;
    $[95] = t25;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  let t27;
  let t28;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = cn("font-semibold", AIFI_DATA_NUMBER_CLASS);
    t28 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[97] = t27;
    $[98] = t28;
  } else {
    t27 = $[97];
    t28 = $[98];
  }
  let t29;
  if ($[99] !== savingSharePercent) {
    t29 = formatScore(savingSharePercent, {
      decimals: 0
    });
    $[99] = savingSharePercent;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  let t30;
  if ($[101] !== t29) {
    t30 = <span className={t27} style={t28}>{t29}%</span>;
    $[101] = t29;
    $[102] = t30;
  } else {
    t30 = $[102];
  }
  let t31;
  if ($[103] !== t10 || $[104] !== t11 || $[105] !== t12 || $[106] !== t13 || $[107] !== t14 || $[108] !== t15 || $[109] !== t16 || $[110] !== t17 || $[111] !== t22 || $[112] !== t26 || $[113] !== t30 || $[114] !== t7 || $[115] !== t8 || $[116] !== t9) {
    t31 = <p className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t22}{" "}could use lighter, more cost-efficient models at comparable quality. Using the optimized model for each task could save{" "}{t26}{" "}a year, or{" "}{t30}{" "}of actual spend.</p>;
    $[103] = t10;
    $[104] = t11;
    $[105] = t12;
    $[106] = t13;
    $[107] = t14;
    $[108] = t15;
    $[109] = t16;
    $[110] = t17;
    $[111] = t22;
    $[112] = t26;
    $[113] = t30;
    $[114] = t7;
    $[115] = t8;
    $[116] = t9;
    $[117] = t31;
  } else {
    t31 = $[117];
  }
  let t32;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <p className="mb-3 flex items-center text-[10px] font-medium tracking-wider text-muted-foreground uppercase">Recommended model tier mix<InfoTooltip content={SAVINGS_DETAILS_SECTION_TOOLTIPS.recommendedMix} label="What the recommended tier mix means" /></p>;
    $[118] = t32;
  } else {
    t32 = $[118];
  }
  let t33;
  if ($[119] !== recommendedMix) {
    t33 = <div className="mt-5">{t32}<RecommendedMixLabel mix={recommendedMix} variant="cards" /></div>;
    $[119] = recommendedMix;
    $[120] = t33;
  } else {
    t33 = $[120];
  }
  let t34;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <p className="mb-3 text-[10px] font-medium tracking-wider text-muted-foreground uppercase">Actual vs optimal catalog cost mix</p>;
    $[121] = t34;
  } else {
    t34 = $[121];
  }
  let t35;
  if ($[122] !== actualMix || $[123] !== optimalMix) {
    t35 = <div className="-mx-6 mt-6 border-t border-t-lightborder px-6 pt-6">{t34}<ModelMixBars actualMix={actualMix} optimalMix={optimalMix} showLabels={true} /></div>;
    $[122] = actualMix;
    $[123] = optimalMix;
    $[124] = t35;
  } else {
    t35 = $[124];
  }
  let t36;
  if ($[125] !== isCalculationLoading || $[126] !== resolvedCalculationRows || $[127] !== tableTotalActualSpend || $[128] !== tableTotalDelta || $[129] !== tableTotalOptimalSpend) {
    t36 = <div className="mt-6"><CalculationTable rows={resolvedCalculationRows} totalActualSpend={tableTotalActualSpend} totalOptimalSpend={tableTotalOptimalSpend} totalDelta={tableTotalDelta} isLoading={isCalculationLoading} /></div>;
    $[125] = isCalculationLoading;
    $[126] = resolvedCalculationRows;
    $[127] = tableTotalActualSpend;
    $[128] = tableTotalDelta;
    $[129] = tableTotalOptimalSpend;
    $[130] = t36;
  } else {
    t36 = $[130];
  }
  let t37;
  if ($[131] !== t18 || $[132] !== t19 || $[133] !== t31 || $[134] !== t33 || $[135] !== t35 || $[136] !== t36) {
    t37 = <div className={t18}>{t19}{t31}{t33}{t35}{t36}</div>;
    $[131] = t18;
    $[132] = t19;
    $[133] = t31;
    $[134] = t33;
    $[135] = t35;
    $[136] = t36;
    $[137] = t37;
  } else {
    t37 = $[137];
  }
  let t38;
  if ($[138] !== t20 || $[139] !== t21 || $[140] !== t37) {
    t38 = <div className={t20}>{t21}{t37}</div>;
    $[138] = t20;
    $[139] = t21;
    $[140] = t37;
    $[141] = t38;
  } else {
    t38 = $[141];
  }
  return t38;
}
function _temp2(sum_0, row_0) {
  return sum_0 + row_0.optimalSpend;
}
function _temp(sum, row) {
  return sum + row.actualSpend;
}
export function buildGlobalDrawerSubtitle(data: SavingsScopeData): string {
  return `Calculated over ${formatCount(data.interactionsCount)} interactions in the last 30 days · ${formatScore(data.frontierSharePercent, {
    decimals: 0
  })}% of spend on frontier models`;
}
export function buildTaskDrawerSubtitle(task: SavingsTask): string {
  return `Calculated over ${formatCount(task.interactionsCount)} interactions in the last 30 days · ${formatScore(task.frontierSharePercent, {
    decimals: 0
  })}% of spend on frontier models`;
}